import { Component, OnInit, ViewChild } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { AuthHelperService } from 'src/app/services/auth-helper.service';
import { CustomerService } from 'src/app/services/customer.service';
import { WellnessWorkOrder, AdvancedWorkOrderFilter, WellnessWorkOrderGenSvc, WorkOrderListView, EmployeeGenSvc, ZipCode, ZipCodeGenSvc } from 'src/app/services_autogenerated/generated_services';
import { WorkOrderFilterBarComponent } from '../../work-order-filter-bar/work-order-filter-bar.component';
import { WorkOrderFilterPipe } from 'src/app/pipes/work-order-filter.pipe';
import { forkJoin, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-wellness-renewal-dashboard',
  templateUrl: './wellness-renewal-dashboard.component.html',
  styleUrls: ['./wellness-renewal-dashboard.component.css'],
  providers: [WorkOrderFilterPipe],
  standalone: false
})
export class WellnessRenewalDashboardComponent implements OnInit {
  biannualTasks: string[] = ['Borer: Emerald Ash Borer Systemic [Spring]','Borer: Emerald Ash Borer Tree-age','Dutch Elm Disease','Oak Wilt','Plant Growth Regulator'];

  @ViewChild(Table) table: Table;

  totalRecords: number;
  rows: number = 30;
  cols: any[];
  allWorkOrders: WorkOrderListView[];
  @ViewChild(WorkOrderFilterBarComponent) filterBarComponent: WorkOrderFilterBarComponent;

  rms: any[];
  zipCodes: any[];
  equipmentTypes: any[] = [];
  canSeeHiddenPrice: boolean;
  claims: string[];
  hideRMAndZip: boolean = true;

  isLoading: boolean = true;

  lastLazyEvent: LazyLoadEvent;

  advancedFilters: AdvancedWorkOrderFilter = new AdvancedWorkOrderFilter({
    shouldFilterByRM: false,
    shouldFilterByDueDate: false,
    shouldFilterByGeoRegion: false,
    shouldFilterByPriority: false,
    shouldFilterByGDD: false,
    shouldFilterByAutoRenewalSelected: false,
    shouldFilterByNeedsRenewed: false,
    shouldFilterByZip: false,
    shouldFilterByCreatedDate: false,
    shouldFilterByEquipmentType: false,
    shouldFilterByCompletedDate: false,
    rMs: [],
    priorities: [],
    skip: 0,
    take: this.rows,
    globalFilter: '',
    orderBy: 'FullName ASC'
  });

  constructor(
    private authHelperService: AuthHelperService,
    public customCustomerService: CustomerService,
    private wellnessWorkOrderService: WellnessWorkOrderGenSvc,
    private employeeService: EmployeeGenSvc,
    private zipCodeService: ZipCodeGenSvc,
  ) {
    this.cols = [
      { field: 'FullName', header: 'Customer Name', width: '15%' },
      { field: 'Number', header: 'Number', width: '10%' },
      { field: 'CreatedOn', header: 'Created On', width: '10%' },
      { field: 'Status', header: 'Status', width: '10%' },
      { field: 'CompletedDate', header: 'Completed Date', width: '10%' },
      { field: 'Zip', header: 'Zip Code', width: '10%' },
      { field: 'TaskNames', header: 'Tasks', width: '18%', info: 'Bolded task names should be scheduled every other year.' },
      { field: 'AnyRenewalAccepted', header: 'Auto Renewal Accepted', width: '10%' },
      { field: 'AnyNeedsRenewed', header: 'Needs Renewed', width: '10%' },
      { header: '', width: '7%' }
    ];

    const token = this.authHelperService.getDecodedAccessToken();

    forkJoin([
      this.zipCodeService.getAll().pipe(
        catchError((error: any) => {
          return throwError(error);
        })
      ), 
      this.employeeService.getRegionalManagers().pipe(
        catchError((error: any) => {
          return throwError(error);
        })
      ), 
    ])
    .subscribe(res => {
      // todo by claim, not role
      if (token.claimNames.includes('View All Renewals')) {
        this.zipCodes = res[0].filter(z => z.code != '0');
        this.rms = res[1];
        this.hideRMAndZip = false;
      } else if (token.claimNames.includes('View Assigned Renewals')) {
        const employee = res[1].filter(emp => emp.id == token.id)[0];
        this.zipCodes = employee.zipCodeAssignments.filter(zca => zca.zipCode.code != '0').map(zca => zca.zipCode);
        this.rms = [employee];

        this.filterBarComponent.selectedZipCodes = employee.zipCodeAssignments.filter(zca => zca.zipCode.code != '0').map(zca => zca.zipCode);
        this.filterBarComponent.shouldFilterByZip = true;
        this.advancedFilters.zipCodes = this.filterBarComponent.selectedZipCodes.map(z => z.code);
        this.advancedFilters.shouldFilterByZip = true;
        
        this.hideRMAndZip = true;
      }
      
      this.filterBarComponent.shouldFilterByQuoteDeclined = true;
      this.advancedFilters.shouldFilterByQuoteDeclined = true;

      this.filterBarComponent.createdDateStart = new Date(new Date().getFullYear() - 1, 0, 1);
      this.filterBarComponent.createdDateEnd = new Date(new Date().getFullYear() - 1, 11, 31);
      this.filterBarComponent.shouldFilterByCreatedDate = true;

      this.advancedFilters.createdDateStart = new Date(new Date().getFullYear() - 1, 1, 1);
      this.advancedFilters.createdDateEnd = new Date(new Date().getFullYear() - 1, 11, 31);
      this.advancedFilters.shouldFilterByCreatedDate = true;        
    });

    this.canSeeHiddenPrice = token.claimNames.includes('View Hidden Prices');
  }

  ngOnInit() {
    this.claims = this.authHelperService.getDecodedAccessToken().claimNames;
  }

  loadRequestLazy(event: LazyLoadEvent) {
    this.isLoading = true;
    this.lastLazyEvent = event;
    const sortOption = this.getSortOption(event.sortField, event.sortOrder);

    this.advancedFilters.skip = event.first;
    this.advancedFilters.take = event.rows;
    this.advancedFilters.orderBy = sortOption;
    this.advancedFilters.globalFilter = this.filterBarComponent.searchTerm ? this.filterBarComponent.searchTerm : '';

    this.wellnessWorkOrderService.getPaginatedRenewals(this.advancedFilters).subscribe(res => {
      this.allWorkOrders = res.results;
      this.totalRecords = res.totalResults;
      this.isLoading = false;
    });
  }

  OnWoFilteringFinished(forceRefresh: boolean = false) {
    const shouldRMFilter = this.advancedFilters.shouldFilterByRM !== this.filterBarComponent.shouldFilterByRM || this.filterBarComponent.shouldFilterByRM;
    this.advancedFilters.shouldFilterByRM = this.filterBarComponent.shouldFilterByRM;
    this.advancedFilters.rMs = this.filterBarComponent.selectedRMs.map(e => e.fullName);

    const shouldDueDateFilter = this.advancedFilters.shouldFilterByDueDate !== this.filterBarComponent.shouldFilterByDueDate || this.filterBarComponent.shouldFilterByDueDate;
    this.advancedFilters.shouldFilterByDueDate = this.filterBarComponent.shouldFilterByDueDate;

    if (shouldDueDateFilter) {
      if (this.filterBarComponent.dueDateFilterFromDate) {
        this.advancedFilters.startDate = new Date(this.filterBarComponent.dueDateFilterFromDate);
      }

      if (this.filterBarComponent.dueDateFilterToDate) {
        this.advancedFilters.endDate = new Date(this.filterBarComponent.dueDateFilterToDate);
      }
    }

    const shouldPriorityFilter = this.advancedFilters.shouldFilterByPriority !== this.filterBarComponent.shouldFilterByPriority || this.filterBarComponent.shouldFilterByPriority;
    this.advancedFilters.shouldFilterByPriority = this.filterBarComponent.shouldFilterByPriority;
    this.advancedFilters.priorities = this.filterBarComponent.selectedPriorities.map(e => e.status);

    const shouldGeoRegionFilter = (this.advancedFilters.shouldFilterByGeoRegion !== this.filterBarComponent.shouldFilterByGeoRegion || this.filterBarComponent.shouldFilterByGeoRegion)
                                  && this.filterBarComponent.geoRegionWorkOrderNumber && this.filterBarComponent.geoRegionSearchRadius;
    this.advancedFilters.shouldFilterByGeoRegion = this.filterBarComponent.shouldFilterByGeoRegion;
    this.advancedFilters.geoRegionMiles = this.filterBarComponent.geoRegionSearchRadius;
    this.advancedFilters.geoRegionOrigQuoteId = this.filterBarComponent.geoRegionWorkOrderNumber;

    const shouldGDDFilter = (this.advancedFilters.shouldFilterByGDD !== this.filterBarComponent.shouldFilterByGDD || this.filterBarComponent.shouldFilterByGDD);
    this.advancedFilters.shouldFilterByGDD = this.filterBarComponent.shouldFilterByGDD;

    if (shouldGDDFilter) {
      this.advancedFilters.gDDStart = this.filterBarComponent.gddStart;
      this.advancedFilters.gDDEnd = this.filterBarComponent.gddEnd;
    }

    const shouldFilterByAutoRenewalSelected = this.advancedFilters.shouldFilterByAutoRenewalSelected !== this.filterBarComponent.shouldFilterByAutoRenewalSelected || this.filterBarComponent.shouldFilterByAutoRenewalSelected;
    this.advancedFilters.shouldFilterByAutoRenewalSelected = this.filterBarComponent.shouldFilterByAutoRenewalSelected;

    const shouldFilterByNeedsRenewed = this.advancedFilters.shouldFilterByNeedsRenewed !== this.filterBarComponent.shouldFilterByNeedsRenewed || this.filterBarComponent.shouldFilterByNeedsRenewed;
    this.advancedFilters.shouldFilterByNeedsRenewed = this.filterBarComponent.shouldFilterByNeedsRenewed;

    const shouldTextFilter = this.advancedFilters.globalFilter !== this.filterBarComponent.searchTerm;

    const shouldFilterByZip = this.advancedFilters.shouldFilterByZip !== this.filterBarComponent.shouldFilterByZip || this.filterBarComponent.shouldFilterByZip;
    this.advancedFilters.shouldFilterByZip = this.filterBarComponent.shouldFilterByZip;
    this.advancedFilters.zipCodes = this.filterBarComponent.selectedZipCodes.map(e => e.code);

    if (!this.filterBarComponent.geoRegionWorkOrderNumber) {
      this.filterBarComponent.geoRegionWorkOrderFindError = 'Please enter Number';
    } else if (!this.filterBarComponent.geoRegionSearchRadius) {
      this.filterBarComponent.geoRegionWorkOrderFindError = 'Please enter miles';
    } else {
      this.filterBarComponent.geoRegionWorkOrderFindError = '';
    }

    const shouldFilterByCreatedDate = (this.advancedFilters.shouldFilterByCreatedDate !== this.filterBarComponent.shouldFilterByCreatedDate || this.filterBarComponent.shouldFilterByCreatedDate);
    this.advancedFilters.shouldFilterByCreatedDate = this.filterBarComponent.shouldFilterByCreatedDate;

    if (shouldFilterByCreatedDate) {
      this.advancedFilters.createdDateStart = this.filterBarComponent.createdDateStart;
      this.advancedFilters.createdDateEnd = this.filterBarComponent.createdDateEnd;
    }

    const shouldFilterByCompletedDate = (this.advancedFilters.shouldFilterByCompletedDate !== this.filterBarComponent.shouldFilterByCompletedDate || this.filterBarComponent.shouldFilterByCompletedDate);
    this.advancedFilters.shouldFilterByCompletedDate = this.filterBarComponent.shouldFilterByCompletedDate;

    if (shouldFilterByCompletedDate) {
      this.advancedFilters.completedDateStart = this.filterBarComponent.completedDateStart;
      this.advancedFilters.completedDateEnd = this.filterBarComponent.completedDateEnd;
    }
    console.log(shouldFilterByCompletedDate)
    console.log(this.advancedFilters.completedDateStart)
    console.log(this.advancedFilters.completedDateEnd)


    if (forceRefresh || shouldRMFilter || shouldDueDateFilter || shouldGeoRegionFilter || shouldPriorityFilter || shouldGDDFilter 
      || shouldTextFilter || shouldFilterByAutoRenewalSelected || shouldFilterByNeedsRenewed || shouldFilterByZip || shouldFilterByCreatedDate || shouldFilterByCompletedDate) {
      const lazyRequest = {
        ...this.lastLazyEvent,
        first: forceRefresh ? this.table.first : 0,
        rows: this.rows
      };
      this.table.first =  forceRefresh ? this.table.first : 0;
      this.loadRequestLazy(lazyRequest);
    }
  }

  edit(link: string) {
    window.open(link, '_blank');
  }

  getSortOption(field: string, order: number) {
    if (order === 1) {
      return field + ' ASC';
    } else {
      return field + ' DESC';
    }
  }

  private getUniqueNames(arr: any[]) {
    const unique = arr.map((e, i, final) => final.indexOf(e) === i && i)

      // eliminate the dead keys & store unique objects
      .filter(e => arr[e]).map(e => arr[e]).sort();

    return unique.map(u => ({fullName: u}));
  }
}
